import { useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import useCustomerDetails from "../../../data/useCustomerDetails";
import useOffers from "../../../data/useOffers";
import usePartMutations from "../../../data/usePartMutations";
import usePartsLookupMutations from "../../../data/usePartsLookupMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { FormatType, IEbayOffer } from "../../../models/EbayOffer";
import { IPart } from "../../../models/Part";
import useNotification from "../../notifications/useNotifications";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import { PartProPriceButton } from "../../shared/PriceButtons";
import UnlistedTablePriceButton from "./UnlistedTablePriceButton";
import PriceFormat from "../../../utils/priceFormat";
import { useUserLookup } from "../../../hooks/useUserLookup";
import { DateTime } from "luxon";

export default function UnlistedOfferPrice({ part, showButtons = true }: { part: IPart; showButtons?: boolean; }) {
  const queryClient = useQueryClient();
  const { update } = useOffers();
  const { createOffer, patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Price");
  const { addNotification } = useNotification();
  const { lookupMutation } = usePartsLookupMutations(part.id);
  const { brand } = useCustomerDetails();

  const [isEditing, setIsEditing] = useState(false);
  const [partProOpen, setPartProOpen] = useState(false);
  const [partProLoading, setPartProLoading] = useState(false);

  const handlePriceUpdate = (price: string | number) => {
    let doublePrice = typeof price === "number" ? price : parseFloat(price);

    if (isNaN(doublePrice)) return;

    doublePrice = Math.round(doublePrice * 100) / 100;

    if (part.offers && part.offers.length > 0) {
      const updatedOffer = {
        ...part.offers[0],
      };

      if (part.offers[0].enableBestOffer && doublePrice < (part.offers[0].autoAcceptPrice || 0)) {
        addNotification({ primaryText: "Best Offer Enabled - cannot set price below auto accept price", variant: "info" });
        return;
      }

      updateOffer(updatedOffer, doublePrice);
    } else {
      updatePart(part, doublePrice);
    }
  }

  const updatePart = (part: IPart, price: number) => {
    patchPart.mutate({
      id: part.id,
      enteredPrice: price
    }, {
      ...saveHandlers,
      onSuccess: (data) => {
        part.enteredPrice = price;
        saveHandlers.onSuccess();
        queryClient.invalidateQueries(["parts", part.id]);
        setIsEditing(false);
      }
    });
  }

  const updateOffer = (offer: IEbayOffer, price: number) => {
    if (offer.format === FormatType.Auction) {
      offer.auctionStartPrice = price;
    } else {
      offer.buyItNowPrice = price;
    }

    update.mutate(offer, {
      ...saveHandlers,
      onSuccess: (data) => {
        updatePrice(part.offers.find((offer) => offer.id === offer.id)!, data.data.buyItNowPrice);
        saveHandlers.onSuccess();
        queryClient.invalidateQueries(["parts", part.id]);
        setIsEditing(false);
      }
    });
  }

  const updatePrice = (offer: IEbayOffer, price: number) => {
    if (offer.format === FormatType.Auction) {
      offer.auctionStartPrice = price;
    } else {
      offer.buyItNowPrice = price;
    }
  }

  const handlePartProLookup = () => {
    if (part?.partsLookupRequestId) {
      setPartProOpen(false);
      return;
    }

    setPartProLoading(true);
    lookupMutation.mutate(undefined,
      {
        onSuccess: (data) => {
          if (!data.data.requestedParts || data.data.requestedParts.length === 0) {
            addNotification({ primaryText: "No Parts Found", variant: "info", });
            setPartProLoading(false);
            return;
          }

          part.suggestedPrice = data.data.requestedParts?.find((p) => p.partCategory?.id === part.category!.id)?.priceInformation.avgPrice || 0;
          part.lastPartLookupChecked = DateTime.now().toISO();

          addNotification({ primaryText: "PartPro Lookup Successful", variant: "success", });
        },
        onError: (error) => {
          console.error("PartPro lookup failed:", error);
          addNotification({ primaryText: "PartPro Lookup Failed", variant: "error", })
        }
      }
    );

    setPartProOpen(false);
  }

  const isLocal = process.env.NODE_ENV === "development";

  const { findUser, isLoading: userLookupLoading } = useUserLookup();

  const pricedBy = useMemo(() => {
    if (!part.pricedBy || userLookupLoading) return null;

    const user = findUser(part.pricedBy);
    return user?.fullname;
  }, [part])

  // derive rendering state
  const hasOffers = part.offers && part.offers.length > 0;
  const label = hasOffers
    ? part.offers[0]?.format === FormatType.Auction
      ? "Auction"
      : "Buy It Now"
    : `Buy It Now ${isLocal ? "**" : ""}`;
  const inputValue = hasOffers
    ? part.offers[0]?.format === FormatType.Auction
      ? part.offers[0]?.auctionStartPrice
      : part.offers[0]?.buyItNowPrice
    : part.enteredPrice;

  return (
    <>
      <ConfirmModal
        open={partProOpen}
        setOpen={setPartProOpen}
        onConfirm={handlePartProLookup}
        isLoading={partProLoading}
        title={"Activate PartPro Lookup?"}
        message={`This will start a PartPro lookup on this vehicle, this may take some time.\n
                  This will check against an exact part number match, display it for you in the table, show how many it matched on and give you an accurate price estimate for the parts.\n
                  This costs 1 token per lookup per vehicle (multiple parts from the same vehicle count as one lookup), you have ${brand.data?.data.lookupTokens ?? 0
          } tokens remaining.`}
        confirmButtonText={null}
      />

      <div
        className="flex flex-col items-center gap-y-2"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-xs text-gray-500">{label}</p>
        <div title={pricedBy ? `Priced By: ${pricedBy}` : ""} className="flex items-center">
          {isEditing ? (
            <>
              <span className="mr-px">£</span>
              <input
                key={part.id}
                defaultValue={inputValue ?? undefined}
                step={1}
                className="no-spinner h-8 w-20 border p-1 text-sm"
                type="number"
                onBlur={(e) => handlePriceUpdate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur(); // removes focus → triggers onBlur
                  }
                }}
              />
            </>
          ) : (
            <div
              className="h-8 w-20 border p-1 text-sm flex hover:bg-gray-200 hover:scale-110 items-center cursor-pointer transition-all"
              onClick={() => setIsEditing(true)}
            >
              {inputValue != null ? PriceFormat(inputValue) : "-"}
            </div>
          )}
        </div>

        {showButtons && (
          <div className="flex flex-col space-y-1 pl-2">
            <PartProPriceButton
              part={part}
              onPriceSelect={handlePriceUpdate}
              partProUnlocked={
                part.partsLookupRequestId != null ||
                part.lastPartLookupChecked != null
              }
              partProLoading={partProLoading}
              onLockedClick={() => setPartProOpen(true)}
            />

            <UnlistedTablePriceButton
              price={part.marketDataPrice}
              onPriceSelect={handlePriceUpdate}
              colour="bg-[#9b0071]"
              title="Market Data Price"
            />

            <UnlistedTablePriceButton
              price={part.lastSoldPrice}
              onPriceSelect={handlePriceUpdate}
              title="Last Sold Price"
            />
          </div>
        )}
      </div>
    </>
  );
}


//Old display
// <span>
//   {
//     info.getValue()?.at(0)?.format === FormatType.Auction ?
//       PriceFormat(info.getValue()?.at(0)?.auctionStartPrice) :
//       PriceFormat(info.getValue()?.at(0)?.buyItNowPrice)
//   }
// </span>,